/* eslint-disable no-extra-parens */
import { utilities } from '@weatherbug/common';
import { configureAds, configurePageAdSlots, getAdConfig, clearAdRequest } from '../../common/actions/displayAdActions';
import { reduxConnect } from '../../common';
import { WbStatePrivacyCookieKey, ComplianceRequirements, GlobalAdConfigs, IStdParameters, IStoreState } from '../../components/common/interfaces/types';
import { useAdScriptLoader } from './adScriptsLoader';
import { useDevice } from '../../components/common/utilities/useDevice';
import { useEffect, useRef } from 'react';
import { addPartnerIdToIdentityHubModule, setPrebidEventListeners } from './adManagerUtils';
import { getStatePrivacyPolicyCookie } from '../../common/utilities/ccpaHelper';

const { unitSystemsEnum } = utilities.units;

type AdManagerWrapperProps = {
    adsConfigured: boolean;
    enableAds: boolean;
    pageId: string;
    popupId: string;
    storyArticleId: string;
    userAge: number;
    userGender: number;
    nAgt: string;
    os: string;
    osVersion: string;
    languageCode: string;
    appID: string;
    appVersion: string;
    adTarget: any;
    doNotTrack: boolean;
    globalAdConfig: GlobalAdConfigs;
    adConfigLoading: boolean;
    authId: string;
    secret: string;
    stdParameters: IStdParameters;
    complianceRequirements: ComplianceRequirements;
    shouldLoadAdScript: boolean;
    latitude: number;
    longitude: number;
    region: string;
    city: string;
    state: string;
    country: string;
    zip: string;
    configureAds: typeof configureAds;
    configurePageAdSlots: typeof configurePageAdSlots;
    getAdConfig: typeof getAdConfig;
    clearAdRequest: typeof clearAdRequest;
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    adTargetingBaseUrl: string;
    stationId: string;
    providerId: string;
    adConfigLoadError: any;
    unitSystem: string;
    sourceProviderId: string;
    refreshTargetingAPICalls: boolean;
    gpcCheckComplete: boolean;
    gpcEnabled: boolean;
    activeRoute: string;
    cam: string;
    camId: string;
};

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { statePrivacy, legal, displayAd, units } = storeState || {};
    const { appState } = storeState?.appState || {};
    
    const {
        pageId,
        popupId,
        routeParams,
        nAgt,
        os,
        osVersion,
        languageCode,
        appID,
        doNotTrack,
        authId,
        secret,
        adTarget,
        appVersion,
        adTargetingBaseUrl,
        stdParameters,
        enableAds,
        sourceProviderId,
        user,
        activeRoute
    } = appState || {};
    const unitSystem = units ? unitSystemsEnum[units.unitSystem as keyof typeof unitSystemsEnum] : 1;
    const locality = user?.ActiveLocation?.Locality || {};
    const station = user?.ActiveLocation?.Station || {};
    const userIsBot = user?.IsBot;

    const complianceRequirements = legal?.complianceRequirements;

    return {
        pageId,
        popupId,
        storyArticleId: routeParams?.id,
        userAge: user?.AgeValue,
        userGender: user?.GenderValue,
        nAgt,
        os,
        osVersion,
        languageCode,
        appID,
        doNotTrack,
        globalAdConfig: displayAd?.adConfig,
        adConfigLoadError: displayAd?.adConfigLoadError,
        adConfigLoading: displayAd?.adConfigIsFetching,
        adsConfigured: displayAd?.adsConfigured,
        authId,
        secret,
        latitude: locality?.Latitude,
        longitude: locality?.Longitude,
        region: locality?.Dma,
        city: locality?.CityName,
        state: locality?.TerritoryAbbr,
        country: locality?.CountryShortName,
        zip: locality?.PostalCode,
        adTarget,
        appVersion,
        adTargetingBaseUrl,
        stdParameters,
        stationId: station?.StationId,
        providerId: station?.ProviderId,
        enableAds: !userIsBot && enableAds,
        unitSystem,
        sourceProviderId,
        refreshTargetingAPICalls: statePrivacy?.refreshTargetingAPICalls,
        gpcCheckComplete: statePrivacy?.gpcCheckComplete,
        shouldLoadAdScript: !userIsBot && complianceRequirements && (!complianceRequirements?.requiresGDPRCompliance && !complianceRequirements?.disableAds),
        gpcEnabled: statePrivacy?.gpcEnabled,
        activeRoute, 
        cam: routeParams?.cam,
        camId: routeParams?.camId,
        complianceRequirements
    };
};

const mapDispatchToProps = {
    configureAds,
    configurePageAdSlots,
    getAdConfig,
    clearAdRequest
};

export const AdManagerWrapper = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({
    pageId,
    popupId,
    userAge,
    userGender,
    nAgt,
    os,
    osVersion,
    languageCode,
    appID,
    doNotTrack,
    globalAdConfig,
    adConfigLoadError,
    adsConfigured,
    adConfigLoading,
    authId,
    secret,
    latitude,
    longitude,
    region,
    city,
    state,
    country,
    zip,
    adTarget,
    appVersion,
    adTargetingBaseUrl,
    stdParameters,
    stationId,
    providerId,
    enableAds,
    unitSystem,
    sourceProviderId,
    refreshTargetingAPICalls,
    gpcCheckComplete,
    gpcEnabled,
    activeRoute, 
    cam,
    camId,
    complianceRequirements,
    shouldLoadAdScript,
    getAdConfig,
    clearAdRequest,
    configureAds,
    configurePageAdSlots
}: AdManagerWrapperProps) => {
    const xandrPrebidScriptsLoaded = useAdScriptLoader(shouldLoadAdScript);
    const { isDesktop, isMobile, isTablet, deviceTypeKey } = useDevice();
    const didAddPrebidEvents = useRef(false);
    const didAddIdentityModule = useRef(false);
    const canStartProcessingAdsConfigs = useRef(false);
    const prebidEventCallbackRef = useRef<(...params: any[]) => void>();
    const adConfigsLoadedRef = useRef(false);
    const prevProps = useRef<{
        pageId?: string;
        latitude?: number;
        longitude?: number;
        adsConfigured?: boolean;
    }>({});

    useEffect(() => {
        if (!pageId) {
            return;
        }
        
        if (xandrPrebidScriptsLoaded && !canStartProcessingAdsConfigs.current) {
            canStartProcessingAdsConfigs.current = true;
            if (!didAddIdentityModule.current) {
                didAddIdentityModule.current = true;
                addPartnerIdToIdentityHubModule();
            }

            if (!didAddPrebidEvents.current) {
                prebidEventCallbackRef.current =  (adUnits: any) => {
                    const statePrivacyCookie = getStatePrivacyPolicyCookie(WbStatePrivacyCookieKey);
                    const userids = window.wb_web_ihowpbjs?.getUserIds?.() ?? {};
                    if (!pageId) {
                        return;
                    }
                    if (!statePrivacyCookie) {
                        adUnits?.forEach((adUnit: any) => {
                            const gpId = popupId ? `${adUnit.code}/${pageId}/${popupId}` : `${adUnit.code}/${pageId}`;
                
                            adUnit.bids?.forEach((bid: any) => {
                                bid.userId = {
                                    ...bid.userId,
                                    ...userids
                                };
                            });
                            adUnit.ortb2Imp = {
                                ...adUnit.ortb2Imp,
                                ext: {
                                    ...adUnit?.ortb2Imp?.ext,
                                    gpid: gpId
                                }
                            };
                        });
                    } else if (Object.keys(userids).length && statePrivacyCookie) {
                        adUnits?.forEach((adUnit: any) => {
                            adUnit.bids?.forEach((bid: any) => {
                                bid.userId = {};
                                bid.userIdAsEids = [];
                            });
                        });
                    }
                };

                setPrebidEventListeners('on', prebidEventCallbackRef.current);
                didAddPrebidEvents.current = true;    
            }
        }
        
        return () => {
            didAddPrebidEvents.current = false;
            if (prebidEventCallbackRef.current) {
                setPrebidEventListeners('off', prebidEventCallbackRef.current);   
            }
        };
    }, [xandrPrebidScriptsLoaded, pageId, popupId]);
    
    useEffect(() => {
        if (enableAds
            && xandrPrebidScriptsLoaded
            && canStartProcessingAdsConfigs.current 
            && getAdConfig 
            && !globalAdConfig?.pageAdConfigs?.length 
            && authId
            && secret
            && !adConfigsLoadedRef.current 
            && !adConfigLoading) {
            getAdConfig(authId, secret);
            adConfigsLoadedRef.current = true; 
        }
    }, [authId, secret, globalAdConfig?.pageAdConfigs?.[0]?.pageId, adConfigLoading, enableAds, xandrPrebidScriptsLoaded]);

    const configurePageAdOptions = () => {
        if (!enableAds || !gpcCheckComplete || !adConfigsLoadedRef.current || !globalAdConfig?.memberId) {
            return;
        }

        if (Number.isFinite(latitude)
            && Number.isFinite(longitude)
            && authId
            && secret
            && stdParameters
            && gpcCheckComplete
            && adTargetingBaseUrl
            && enableAds) {
         
            const statePrivacyCookie = getStatePrivacyPolicyCookie(WbStatePrivacyCookieKey);
            const adConfig = {
                userAge,
                userGender,
                nAgt,
                os,
                osVersion,
                languageCode,
                appID,
                doNotTrack: statePrivacyCookie?.doNotSell || statePrivacyCookie?.limitUseOfPI ? true : doNotTrack,
                pageId,
                latitude,
                longitude,
                region,
                city,
                state,
                country,
                zip,
                keywords: {
                    V: appVersion
                } as Record<any, any>
            };

            if (isMobile) {
                adConfig.keywords.DVC = 's';
            } else if (isTablet) {
                adConfig.keywords.DVC = 't';
            } else {
                adConfig.keywords.DVC = 'd';
            }

            if (adTarget) {
                adConfig.keywords.PC = adTarget;
            }
            
            if (sourceProviderId) {
                adConfig.keywords.sourceProviderId = sourceProviderId;
            }

            let refreshAdTargetingRequest = !!refreshTargetingAPICalls;

            if (gpcEnabled) {
                refreshAdTargetingRequest = gpcEnabled;
            }

            const adServer = 'appNexus';
            configureAds(globalAdConfig, adConfig, latitude,
                longitude,
                stationId,
                providerId,
                adServer,
                unitSystem,
                authId,
                secret,
                stdParameters,
                adTargetingBaseUrl,
                refreshAdTargetingRequest,
                complianceRequirements);
        }
    };

    const executeConfigurePageAdSlots = () => {
        let cameraId;
        if (activeRoute === 'weatherlandingcamera') {
            cameraId = cam;
        }
        if (activeRoute === 'trafficlandingcamera') {
            cameraId = camId;
        }

        const pageAdConfig = globalAdConfig?.pageAdConfigs?.find(pageConfig => pageConfig?.pageId?.toLowerCase() === pageId?.toLowerCase());
        if (pageAdConfig) {
            configurePageAdSlots(pageId, pageAdConfig, globalAdConfig, latitude, longitude, deviceTypeKey, activeRoute, cameraId);
        }
    };

    useEffect(() => {
        if (!enableAds || !Number.isFinite(latitude) || !Number.isFinite(longitude) || !pageId || !gpcCheckComplete || !adConfigsLoadedRef.current) {
            return;
        }

        if (globalAdConfig?.memberId
            && refreshTargetingAPICalls
            || (pageId !== prevProps.current.pageId) 
            || ((prevProps.current.latitude !== latitude) 
                || (prevProps.current.longitude !== longitude))) {
            configurePageAdOptions();
        }

        if (globalAdConfig?.memberId) {
            prevProps.current.latitude = latitude;
            prevProps.current.longitude = longitude;
            prevProps.current.pageId = pageId;   
        }
    }, [latitude, latitude, pageId, enableAds, gpcCheckComplete, refreshTargetingAPICalls, globalAdConfig?.memberId]);

    useEffect(() => {
        if (!enableAds || !adConfigsLoadedRef.current || !globalAdConfig?.memberId) {
            return;
        }

        if (adsConfigured && typeof prevProps.current.adsConfigured === 'boolean' && adsConfigured !== prevProps.current.adsConfigured) {
            executeConfigurePageAdSlots();
        }
        prevProps.current.adsConfigured = adsConfigured;
    }, [enableAds, adsConfigured, globalAdConfig?.memberId]);
    
    useEffect(() => {
        if (!enableAds || !adConfigsLoadedRef.current || !globalAdConfig?.memberId || !activeRoute) {
            return;
        }

        // SPECIFIC check for Traffic / Weather cameras pages:
        if (!(activeRoute === 'trafficlandingcamera' || activeRoute === 'weatherlandingcamera')) {
            return;   
        }

        if (adsConfigured && typeof prevProps.current.adsConfigured === 'boolean' && adsConfigured !== prevProps.current.adsConfigured) {
            executeConfigurePageAdSlots();
        }
        prevProps.current.adsConfigured = adsConfigured;
    }, [enableAds, adsConfigured, globalAdConfig?.memberId]);

    useEffect(() => {
        if (!enableAds || !gpcCheckComplete || !adsConfigured) {
            return;
        }
    }, [adsConfigured, latitude, longitude, pageId, enableAds, gpcCheckComplete]);
   
    return null;
});

AdManagerWrapper.displayName = 'AdManagerWrapper';